import React from 'react';
import { createPortal } from 'react-dom';

type Option = {
  value: string;
  label: string;
};

type Props = {
  options: Option[];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  forceOpenUp?: boolean;
  panelMinWidth?: number;
  disablePanelMaxHeight?: boolean;
  flushToTriggerTop?: boolean;
};

const SingleSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  forceOpenUp,
  panelMinWidth,
  disablePanelMaxHeight,
  flushToTriggerTop,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties | null>(null);

  const getContentMinWidth = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return undefined;

    const computed = window.getComputedStyle(trigger);
    const font = computed.font || `${computed.fontSize} ${computed.fontFamily}`;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.font = font;

    let max = 0;
    for (const opt of options) {
      const w = ctx.measureText(opt.label).width;
      if (w > max) max = w;
    }

    const paddingAndIcons = 52;
    return Math.ceil(max + paddingAndIcons);
  }, [options]);

  const updatePanelPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 6;
    const maxPanelHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    const openUp = forceOpenUp ? true : spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(maxPanelHeight, openUp ? spaceAbove : spaceBelow));

    const contentMinWidth = getContentMinWidth();
    const desiredWidth = Math.max(
      rect.width,
      panelMinWidth ?? 0,
      contentMinWidth ?? 0
    );

    const maxLeft = window.innerWidth - margin - desiredWidth;
    const left = Math.max(margin, Math.min(rect.left, maxLeft));

    const top = openUp
      ? Math.max(margin, rect.top - margin - (disablePanelMaxHeight ? maxPanelHeight : maxHeight))
      : rect.bottom + margin;

    const bottom = openUp && flushToTriggerTop ? Math.max(0, window.innerHeight - rect.top) : undefined;
    setPanelStyle({
      position: 'fixed',
      top: openUp && flushToTriggerTop ? 'auto' : top,
      bottom,
      left,
      width: desiredWidth,
      zIndex: 3000,
      minWidth: undefined,
      maxHeight: disablePanelMaxHeight ? undefined : maxHeight,
    });
  }, [disablePanelMaxHeight, flushToTriggerTop, forceOpenUp, getContentMinWidth, panelMinWidth]);

  React.useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }

    updatePanelPosition();

    const onDocMouseDown = (e: MouseEvent) => {
      const el = rootRef.current;
      const panelEl = panelRef.current;
      if (!el) return;
      if (!(e.target instanceof Node)) return;

      const isInsideTrigger = el.contains(e.target);
      const isInsidePanel = panelEl ? panelEl.contains(e.target) : false;
      if (!isInsideTrigger && !isInsidePanel) {
        setOpen(false);
      }
    };

    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onViewportChange = () => updatePanelPosition();

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const selectedLabel = React.useMemo(() => {
    const hit = options.find((o) => o.value === value);
    if (hit) return hit.label;
    return placeholder ?? '—';
  }, [options, placeholder, value]);

  const select = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="ms">
      <button
        ref={triggerRef}
        type="button"
        className={['ui-select', 'ms-trigger', className, open ? 'is-open' : undefined].filter(Boolean).join(' ')}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={value ? 'ms-value' : 'ms-placeholder'}>{selectedLabel}</span>
      </button>

      {open && panelStyle
        ? createPortal(
            <div className="ms-panel ms-panel-portal" role="listbox" style={panelStyle} ref={panelRef}>
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={['ms-item', isSelected ? 'is-selected' : undefined].filter(Boolean).join(' ')}
                    onClick={() => select(opt.value)}
                  >
                    <span className={['ms-check', isSelected ? 'is-checked' : undefined].filter(Boolean).join(' ')} />
                    <span className="ms-label">{opt.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SingleSelect;
